import { useContext, useMemo } from 'react'
import type { RateView } from 'components/OfferPage/Property/singlePropertyUtils'
import { getCombinedRates } from 'components/OfferPage/Property/singlePropertyUtils'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import useLPCUniqueOptions from 'hooks/OfferPage/useLPCUniqueOptions'
import { useAppSelector } from 'hooks/reduxHooks'
import { min } from 'lib/array/arrayUtils'
import { initialRooms } from 'lib/offer/occupancyUtils'
import { addFlightPrice, onlyNonZeroFees } from 'lib/offer/pricing'
import {
  arePropertyRatesLoading,
  getPropertyMappedBedbankOffer,
  getPropertyPackageOptionsByRoom,
} from 'selectors/offerPage/offerPageSelectors'

type Return = [
  pricing: App.PricingWithValue | undefined,
  memberPricing: App.PricingWithValue | undefined,
  pricingWithFlights: App.PricingWithValue | undefined,
  memberPricingWithFlights: App.PricingWithValue | undefined,
  rateView: RateView | undefined,
]

// Unfortunately, `EmptyArray` isn't compatible with `Return`.
const emptyReturn: Return = [undefined, undefined, undefined, undefined, undefined]

/**
 * @deprecated This hook (for SPP offers) is being refactored out into selectors. If you _need_ to
 * use this hook, speak to Xiao or Robert.
 *
 * Unlike `<PropertyRoomOption/>`, this hook does _not_ account for GHA-highlighted rates or the
 * `accom_use_property_page_rates_endpoint` feature flag.
 */
function useCheapestPropertyPricing(): Return {
  const offerPageState = useContext(OfferPageStateContext)
  const {
    airport: flightOrigin,
    checkInDate: checkIn,
    checkOutDate: checkOut,
    duration,
    offer,
    rooms = initialRooms,
  } = offerPageState
  const bedbankOffer = useAppSelector(state => getPropertyMappedBedbankOffer(offerPageState, state))
  const packageOptionsByRoom = useAppSelector(state =>
    getPropertyPackageOptionsByRoom(offerPageState, state))
  const isLoading = useAppSelector(state => arePropertyRatesLoading(offerPageState, state))
  const previewMode = useAppSelector(state => state.system.previewMode)

  // Handle LPC
  const combinedOptions = useMemo(
    () => packageOptionsByRoom.flatMap(({ lpc }) => lpc),
    [packageOptionsByRoom],
  )
  const useDynamicCancellationPolicies = offer?.property?.useDynamicCancellationPolicies ?? false
  const requiresRates = useDynamicCancellationPolicies && !!checkIn && !isLoading
  const options = useMemo(() => {
    if (previewMode) return combinedOptions
    return combinedOptions.filter(({ capacityValid, hotelAvailable, hasAvailableRates }) =>
      capacityValid && hotelAvailable && (!requiresRates || hasAvailableRates))
  }, [previewMode, combinedOptions, requiresRates])
  const availableOptions = useLPCUniqueOptions(offer?.id ?? '', options, {
    useDynamicCancellationPolicies,
    duration,
    checkIn,
    rooms,
  })

  // Handle Bedbank
  const bedbankFlightPrice = useBedbankFlightPrice(bedbankOffer, {
    rooms,
    startDate: checkIn,
    endDate: checkOut,
    originAirportCode: flightOrigin?.code,
  })

  // Combine
  const cheapestRoomRates = useMemo(() => packageOptionsByRoom.flatMap(({ lpc, bedbank }) => {
    const combinedRates = getCombinedRates(lpc, bedbank, availableOptions, { previewMode })
    return combinedRates.slice(0, 1)
  }), [packageOptionsByRoom, availableOptions, previewMode])
  return useMemo(() => {
    const cheapestRate = min(cheapestRoomRates, ({ price }) => price)
    if (!cheapestRate) return emptyReturn

    if (cheapestRate.type === 'bedbank') {
      const { totals, value, discount, memberDiscount } = cheapestRate.bedbankRate!.rate
      const fees = onlyNonZeroFees([
        { type: 'taxesAndFees', amount: totals.taxesAndFees },
        { type: 'property', amount: totals.propertyFees ?? 0 },
      ])
      const pricing = {
        price: totals.total,
        fees,
        saleUnit: 'room',
        value,
        discountPercent: discount,
      }
      const memberPricing = (totals.memberTotal && totals.memberTotal > 0) ? {
        price: totals.memberTotal,
        fees,
        saleUnit: 'room',
        value,
        discountPercent: memberDiscount,
      } : undefined
      const pricingWithFlights = bedbankFlightPrice ?
        addFlightPrice(pricing, bedbankFlightPrice) :
        undefined
      const memberPricingWithFlights = (memberPricing && bedbankFlightPrice) ?
        addFlightPrice(memberPricing, bedbankFlightPrice) :
        undefined
      return [pricing, memberPricing, pricingWithFlights, memberPricingWithFlights, cheapestRate]
    }

    const {
      pricing,
      memberPricing,
      pricingWithFlights,
      memberPricingWithFlights,
    } = cheapestRate.lpcRate!
    return [pricing, memberPricing, pricingWithFlights, memberPricingWithFlights, cheapestRate]
  }, [cheapestRoomRates, bedbankFlightPrice])
}

export default useCheapestPropertyPricing
